import { all, dataOf, first, jq, on, parsePrice } from '../core/dom.js';
import { money } from '../core/money.js';

/**
 * Subscription popup funnel (spec §5).
 *
 * The popup is the add-to-cart gate rendered by nf-platform's ATC widget v2
 * (`[data-nf-sub-modal]` inside `.nf-atc2`). Four separate subscription systems sit
 * behind it — COD, Stripe, PayPal and Klarna — which is why the error branch carries
 * `subscription_system`.
 *
 * Dismissed is tracked separately from declined on purpose: closing the popup without
 * reading it is a display or timing problem, while an explicit "one-time purchase" is
 * a problem with the offer. Merging them hides which one you have.
 */

export function collectSubscriptionPopup(tracker, { win = window, doc = document, selectors = {}, data = {} } = {}) {
  const openState = new Map();

  // `data-product-id` is deliberately not in this list. nf-platform's
  // ProductPageWidgets/views/add-to-cart-v2.php emits it on `.nf-atc2` but no SKU
  // attribute, so including it meant dataOf always returned something and the real SKU
  // fallback below was dead — every subscription event reported a numeric product id in
  // `product_sku`, mixing two identifier namespaces in one ClickHouse JSON path.
  const skuFor = (widget) =>
    dataOf(widget, ['data-product-sku', 'data-sku'], null) ||
    (data.viewContent && data.viewContent.product_sku) ||
    undefined;

  const intervalOf = (widget) => {
    const select = first(selectors.subscriptionInterval, widget) || first(selectors.subscriptionInterval, doc);

    if (!select) {
      return 0;
    }

    const option = select.options ? select.options[select.selectedIndex] : null;
    const days = (option && option.getAttribute('data-nf-days')) || select.value;

    return parseInt(days, 10) || 0;
  };

  const opened = (widget) => {
    // The entry lives as long as the modal is on screen, resolved or not — otherwise a
    // single popup reports an impression again after every accept or dismiss.
    if (openState.has(widget)) {
      return;
    }

    openState.set(widget, { at: Date.now(), interval: intervalOf(widget), resolved: false });

    tracker.track('subscription_popup_shown', {
      trigger_type: 'add_to_cart_gate',
      product_sku: skuFor(widget),
      interval_days_shown: intervalOf(widget),
      page_path: tracker.context.page_path,
    });
  };

  const resolve = (widget, event, params) => {
    const state = openState.get(widget) || { at: Date.now(), interval: intervalOf(widget) };
    state.resolved = true;
    openState.set(widget, state);

    tracker.track(
      event,
      Object.assign(
        {
          product_sku: skuFor(widget),
          time_open_s: Math.max(0, Math.round((Date.now() - state.at) / 1000)),
        },
        params
      )
    );
  };

  // Forgetting the widget once the modal is off screen is what allows a genuine second
  // opening to report a second impression.
  const closed = (widget) => {
    openState.delete(widget);
  };

  watchVisibility(doc, selectors, opened, closed, win);

  on(doc, 'change', selectors.subscriptionInterval, (event, el) => {
    const widget = el.closest(selectors.subscriptionWidget) || doc.body;
    const days = parseInt(el.value, 10) || 0;

    tracker.track('subscription_popup_interval_selected', {
      interval_days: days,
      product_sku: skuFor(widget),
      discount_pct: discountPct(widget),
    });
  });

  on(doc, 'click', selectors.subscriptionAccept, (event, el) => {
    const widget = el.closest(selectors.subscriptionWidget) || doc.body;

    resolve(widget, 'subscription_popup_accepted', {
      interval_days: intervalOf(widget),
      value: money(priceOf(widget)),
      payment_method: selectedPaymentMethod(doc),
    });
  });

  on(doc, 'click', selectors.subscriptionDecline, (event, el) => {
    const widget = el.closest(selectors.subscriptionWidget) || doc.body;

    resolve(widget, 'subscription_popup_declined', {
      interval_days_shown: intervalOf(widget),
    });
  });

  on(doc, 'click', selectors.subscriptionDismiss, (event, el) => {
    const widget = el.closest(selectors.subscriptionWidget) || doc.body;

    resolve(widget, 'subscription_popup_dismissed', { dismiss_method: dismissMethod(el) });
  });

  doc.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' && event.keyCode !== 27) {
      return;
    }

    openState.forEach((state, widget) => {
      if (state.resolved || !modalVisible(widget, selectors)) {
        return;
      }

      resolve(widget, 'subscription_popup_dismissed', { dismiss_method: 'escape' });
    });
  });

  collectErrors(tracker, win, doc);

  return openState;
}

/**
 * The modal is opened by removing `hidden` and adding a body class; both are watched
 * so the event reports the moment it is actually on screen rather than page load.
 */
function watchVisibility(doc, selectors, onOpen, onClose, win) {
  const check = () => {
    all(selectors.subscriptionModal, doc).forEach((modal) => {
      const widget = modal.closest(selectors.subscriptionWidget) || modal;

      if (isVisible(modal)) {
        onOpen(widget);
      } else {
        onClose(widget);
      }
    });
  };

  if (typeof win.MutationObserver === 'function') {
    let pending = false;

    // The attribute filter still matches every class change on the page, so the
    // document-wide scan runs at most once per frame.
    const schedule = () => {
      if (pending) {
        return;
      }
      pending = true;

      const run = () => {
        pending = false;
        check();
      };

      if (typeof win.requestAnimationFrame === 'function') {
        win.requestAnimationFrame(run);
      } else {
        win.setTimeout(run, 50);
      }
    };

    new win.MutationObserver(schedule).observe(doc.documentElement, {
      attributes: true,
      subtree: true,
      childList: true,
      attributeFilter: ['hidden', 'aria-hidden', 'class', 'style'],
    });
  }

  check();
}

function isVisible(modal) {
  return !modal.hasAttribute('hidden') && modal.getAttribute('aria-hidden') !== 'true';
}

function modalVisible(widget, selectors) {
  const own = widget.matches && widget.matches(selectors.subscriptionModal) ? widget : null;
  const modal = first(selectors.subscriptionModal, widget) || own;

  return modal ? isVisible(modal) : false;
}

/**
 * subscription_popup_error — a failure while creating or toggling the subscription,
 * which is a technical fault rather than a decision.
 */
function collectErrors(tracker, win = window, doc = document) {
  const $ = jq(win);

  if (!$ || typeof $ !== 'function') {
    return;
  }

  $(doc).on('ajaxError', (_event, xhr, settings) => {
    const body = String((settings && settings.data) || '');

    if (body.indexOf('nf_update_cart_subscription') === -1 && body.indexOf('subscription=') === -1) {
      return;
    }

    tracker.track('subscription_popup_error', {
      error_code: 'http_' + ((xhr && xhr.status) || 0),
      payment_method: selectedPaymentMethod(doc),
      subscription_system: subscriptionSystem(doc),
    });
  });
}

function dismissMethod(el) {
  const className = String(el.className || '');

  return className.indexOf('backdrop') !== -1 ? 'outside_click' : 'close_button';
}

/**
 * Read downward, not upward. `dataOf` walks up the tree, but nf-platform renders
 * data-nf-subscr-save-t1 on `.nf-atc2__opt-subscr-priceCol`, a *descendant* of the widget
 * (ProductPageWidgets/views/add-to-cart-v2.php), so the upward lookup never found it and
 * discount_pct was absent on every subscription_popup_interval_selected.
 */
function discountPct(widget) {
  if (!widget) {
    return undefined;
  }

  const own = widget.getAttribute('data-nf-subscr-save-t1');
  const node = own ? null : widget.querySelector('[data-nf-subscr-save-t1]');
  const value = own || (node && node.getAttribute('data-nf-subscr-save-t1'));

  return value ? parseFloat(String(value).replace(/[^\d.,]/g, '').replace(',', '.')) || undefined : undefined;
}

/**
 * `.nf-atc2__main` is the current price; `.nf-atc2__regular` next to it is the struck
 * original, so it must not be matched first. The class this used to look for,
 * `.nf-atc2__price-main`, appears in no commit of nf-platform — only in the fixture,
 * which had been written to match the selector rather than the storefront.
 */
function priceOf(widget) {
  const node = widget && widget.querySelector('.nf-atc2__main, .nf-atc2__price .amount, .amount');

  return node ? parsePrice(node.textContent) : undefined;
}

function selectedPaymentMethod(doc) {
  const checked = doc.querySelector('input[name="payment_method"]:checked');

  return checked ? String(checked.value) : '';
}

/**
 * cod | stripe | paypal | klarna — the four systems named in the spec.
 */
export function subscriptionSystem(doc = document) {
  const method = selectedPaymentMethod(doc).toLowerCase();

  if (method.indexOf('cod') !== -1) {
    return 'cod';
  }

  if (method.indexOf('stripe') !== -1) {
    return 'stripe';
  }

  if (method.indexOf('paypal') !== -1) {
    return 'paypal';
  }

  if (method.indexOf('klarna') !== -1) {
    return 'klarna';
  }

  return 'unknown';
}
